import React, { useState, useEffect } from 'react';
import { qboFetch } from './qbo';

const ENTITY_LABELS = {
  national: 'REDLINE Fire Watch (HQ)',
  florida: 'REDLINE Florida',
  newjersey: 'REDLINE New Jersey',
  academy: 'FIRE WATCH ACADEMY',
  technologies: 'FIRE WATCH TECHNOLOGIES'
};

export default function QBOConnect() {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  useEffect(() => { loadStatus(); }, []);

  async function loadStatus() {
    setLoading(true);
    try {
      const res = await qboFetch('/qbo/status');
      if (!res.ok) throw new Error('status ' + res.status);
      const data = await res.json();
      setStatuses(data.entities || []);
    } catch {
      setStatuses(Object.keys(ENTITY_LABELS).map(e => ({
        entity: e, qbo_name: ENTITY_LABELS[e], connected: false
      })));
    }
    setLoading(false);
  }

  async function connectEntity(entity) {
    try {
      const res = await qboFetch(`/qbo/auth?entity=${entity}`);
      const data = await res.json();
      if (data.authUrl) {
        window.open(data.authUrl, '_blank', 'width=600,height=700');
        setTimeout(() => loadStatus(), 8000);
      }
    } catch (err) {
      alert('Could not initiate connection: ' + err.message);
    }
  }

  async function triggerSync() {
    setSyncing(true);
    setSyncMsg('');
    try {
      const r = await qboFetch('/qbo/sync', { method: 'POST' });
      if (!r.ok) throw new Error('sync ' + r.status);
      setSyncMsg('Sync triggered — records will update within 1 minute.');
      setTimeout(() => setSyncMsg(''), 8000);
    } catch {
      setSyncMsg('Sync trigger failed — check Worker deployment.');
    }
    setSyncing(false);
  }

  // The Worker refreshes tokens automatically. Only a dead refresh token (needs_reauth) needs a human.
  const connectedCount = statuses.filter(s => s.connected).length;
  const fmtWhen = d => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never';

  return (
    <div>
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <div className="card-title">QuickBooks connection</div>
            <div className="card-hint" style={{ marginTop: '2px' }}>
              {loading ? 'Checking status…' : `${connectedCount} of 5 entities active`}
            </div>
          </div>
          <button onClick={triggerSync} disabled={syncing || connectedCount === 0} className="btn btn-secondary btn-sm">
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        </div>

        {syncMsg && <div className="save-msg save-msg-success" style={{ marginBottom: '14px' }}>{syncMsg}</div>}

        {loading ? <div className="empty-state">Checking QBO connection status…</div> :
          statuses.map(s => {
            const needsReconnect = !!s.needs_reauth;
            const isActive = s.connected && !needsReconnect;

            return (
              <div key={s.entity} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: '1px solid #E2E8F0' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', minWidth: '8px', background: isActive ? '#16A34A' : needsReconnect ? '#D97706' : '#E2E8F0' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>{s.qbo_name}</div>
                  <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '1px' }}>
                    {isActive
                      ? `Connected · cash synced ${fmtWhen(s.cash_synced_at)}${s.cash_error ? ' · last sync error: ' + s.cash_error : ''}`
                      : needsReconnect
                      ? 'QuickBooks revoked access — reconnect required'
                      : 'Not connected'
                    }
                  </div>
                </div>
                {isActive ? (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <span className="badge badge-green">Active</span>
                    <button onClick={() => connectEntity(s.entity)} className="btn btn-secondary btn-sm">Reconnect</button>
                  </div>
                ) : (
                  <button onClick={() => connectEntity(s.entity)} className={`btn btn-sm ${needsReconnect ? 'btn-primary' : 'btn-primary'}`}>
                    {needsReconnect ? 'Reconnect →' : 'Connect →'}
                  </button>
                )}
              </div>
            );
          })
        }
      </div>

      <div className="card">
        <div className="card-title" style={{ marginBottom: '10px' }}>How it works</div>
        <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.7' }}>
          <p style={{ marginBottom: '8px' }}>
            <strong style={{ color: '#0F172A' }}>App → QuickBooks:</strong> Every new dispatch automatically creates a draft Pending Activation estimate in the correct QBO entity.
          </p>
          <p style={{ marginBottom: '8px' }}>
            <strong style={{ color: '#0F172A' }}>QuickBooks → App:</strong> Every 15 minutes, customers, sub-customers, and invoice status sync from all connected entities.
          </p>
          <p style={{ marginBottom: '0' }}>
            <strong style={{ color: '#0F172A' }}>Staying connected:</strong> access tokens renew automatically every hour. You only need to reconnect if QuickBooks revokes access (shown in amber).
          </p>
        </div>
      </div>
    </div>
  );
}
